import type { PageLink } from "../shared/types";

/**
 * Journey gate: wreck this much of the page to summon the elite / boss.
 * The portal itself only opens after that gate foe is dead (no stall weak portal).
 */
export const ELITE_SPAWN_PCT = 0.60;
/** @deprecated Kept for older copy; Journey no longer opens on destruction alone. */
export const PORTAL_OPEN_PCT = ELITE_SPAWN_PCT;
/** Legacy stall thresholds — unused by Journey after the elite gate. */
export const PORTAL_FALLBACK_PCT = 0.70;
export const PORTAL_STALL_MS = 20_000;
/** Share of the document, measured up from the bottom, that counts as the end. */
export const PORTAL_END_BAND = 0.2;

export function portalHint(stage: "wreck" | "elite" | "boss" = "wreck"): string {
  if (stage === "elite") return "Defeat the elite to open a portal";
  if (stage === "boss") return "Defeat the boss to open a portal";
  return `Destroy ${Math.round(ELITE_SPAWN_PCT * 100)}% to summon an elite`;
}

export type GateAffix = "tank" | "summoner" | "rusher";

export type GateStage = "wreck" | "spawned" | "cleared";

/** Early boss floors, then every 5 levels from 12. */
export function isBossFloor(level: number): boolean {
  const n = Math.max(1, Math.floor(level));
  if (n === 2 || n === 4 || n === 7) return true;
  if (n >= 12 && (n - 12) % 5 === 0) return true;
  return false;
}

/**
 * Rotate tank → summoner → rusher.
 * Boss floors use the boss-sequence index so 2/4/7 are Bastion/Nest/Rampage once each,
 * then 12/17/22 continue the same cycle. Normal floors use (level - 1) % 3.
 */
export function gateAffixFor(level: number): GateAffix {
  const lines: GateAffix[] = ["tank", "summoner", "rusher"];
  const n = Math.max(1, Math.floor(level));
  if (isBossFloor(n)) {
    let index = 0;
    for (let i = 1; i < n; i++) if (isBossFloor(i)) index++;
    return lines[index % 3];
  }
  return lines[(n - 1) % 3];
}

export function gateTitle(affix: GateAffix, boss: boolean): string {
  if (boss) {
    if (affix === "tank") return "BOSS · BASTION";
    if (affix === "summoner") return "BOSS · NEST";
    return "BOSS · RAMPAGE";
  }
  if (affix === "tank") return "ELITE · TANK";
  if (affix === "summoner") return "ELITE · SUMMONER";
  return "ELITE · RUSHER";
}


export const PORTAL_FALLBACKS = [
  "https://en.wikipedia.org/wiki/Web_browser",
  "https://en.wikipedia.org/wiki/Stick_figure",
  "https://www.wikipedia.org/",
  "https://example.com/",
  "https://news.ycombinator.com/",
  "https://developer.mozilla.org/en-US/docs/Web/HTML",
  "https://www.mozilla.org/en-US/",
  "https://archive.org/about/",
];

const DOWNLOAD = new Set([
  "pdf", "zip", "rar", "7z", "gz", "tgz", "tar", "bz2", "xz", "exe", "dmg", "pkg", "msi", "apk",
  "iso", "bin", "deb", "rpm", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "mp3", "mp4",
  "wav", "avi", "mov", "mkv", "webm", "epub", "mobi", "psd",
]);

/** Path segments that are login, signup, or other auth walls. */
const AUTH_SEGMENT = /^(login|log-in|signin|sign-in|signup|sign-up|register|oauth|authorize|auth|sso|saml|password|forgot|session|wp-login\.php|sign_in|sign_up)$/i;

const AUTH_HOST = /^(login|signin|signup|auth|oauth|sso|accounts|account|passport|id)\./i;

/** Checkout, donate, and other pages that throw payment widgets instead of a level. */
const COMMERCE_SEGMENT = /^(donate|donations?|checkout|check-out|payment|payments|pay|subscribe|subscription|subscriptions|cart|basket|billing|invoice|invoices)$/i;

const COMMERCE_HOST = /^(donate|checkout|pay|payment|payments|billing|cart|subscribe|store)\./i;

/** Two-label public suffixes so co.uk and friends stay one site. */
const MULTI_SUFFIX = new Set([
  "co.uk", "org.uk", "ac.uk", "gov.uk", "co.jp", "ne.jp", "or.jp", "ac.jp",
  "com.au", "net.au", "org.au", "com.br", "com.cn", "com.tw", "com.hk",
  "co.kr", "co.nz", "co.za", "co.in", "com.mx", "com.ar", "com.tr", "co.il",
  "com.sg", "com.my", "co.id",
]);

export interface PortalSpot {
  href: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** True when the spot is a curated site, not an anchor on this page. */
  fallback: boolean;
}

export function portalHost(href: string): string {
  try {
    return new URL(href).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "site";
  }
}

export function portalLabel(href: string): string {
  return `→ ${portalHost(href)}`;
}

/** Identity used to skip the current page, a failed hop, and a page already walked. */
export function canonUrl(raw: string): string {
  try {
    const url = new URL(raw);
    url.hash = "";
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    const path = url.pathname.replace(/\/+$/, "") || "/";
    return `${url.protocol}//${host}${path}${url.search}`;
  } catch {
    return raw.trim();
  }
}

function privateHost(host: string): boolean {
  const h = host.toLowerCase().replace(/^\[|\]$/g, "").replace(/\.$/, "");
  if (!h || h === "localhost" || h.endsWith(".local") || h.endsWith(".localhost") || h.endsWith(".internal")) return true;
  if (h === "metadata.google.internal" || h.includes("metadata.google.internal")) return true;
  const v4 = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (v4) {
    const [a, b] = v4.slice(1).map(Number);
    if ([a, b].some((n) => n > 255)) return true;
    if (a === 0 || a === 10 || a === 127) return true;
    if (a === 169 && b === 254) return true;
    if (a === 172 && b >= 16 && b <= 31) return true;
    if (a === 192 && b === 168) return true;
    if (a === 100 && b >= 64 && b <= 127) return true;
    if (a === 192 && b === 0) return true;
    if (a === 198 && (b === 18 || b === 19 || b === 51)) return true;
    if (a === 203 && b === 0) return true;
    if (a >= 224) return true;
    return false;
  }
  if (h === "::1" || h === "0:0:0:0:0:0:0:1") return true;
  if (h.startsWith("fc") || h.startsWith("fd") || h.startsWith("fe80")) return true;
  if (h.startsWith("::ffff:")) return privateHost(h.slice(7));
  return false;
}

/** True when `/api/page` would accept the URL: public http(s) on port 80 or 443. */
export function proxyLoadable(raw: string): URL | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.username || url.password) return null;
  const port = url.port ? Number(url.port) : url.protocol === "https:" ? 443 : 80;
  if (port !== 80 && port !== 443) return null;
  if (privateHost(url.hostname)) return null;
  return url;
}

function isDownload(url: URL): boolean {
  const path = url.pathname.toLowerCase();
  const dot = path.lastIndexOf(".");
  if (dot < 0) return false;
  return DOWNLOAD.has(path.slice(dot + 1));
}

function isAuth(url: URL): boolean {
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (AUTH_HOST.test(host)) return true;
  return pathParts(url).some((part) => {
    if (AUTH_SEGMENT.test(part)) return true;
    const token = part.toLowerCase().replace(/[^a-z0-9]/g, "");
    return token.includes("login") || token.includes("signin") || token.includes("signup") || token.includes("oauth");
  });
}

function isCommerce(url: URL): boolean {
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (COMMERCE_HOST.test(host)) return true;
  return pathParts(url).some((part) => {
    if (COMMERCE_SEGMENT.test(part)) return true;
    const token = part.toLowerCase().replace(/[^a-z0-9]/g, "");
    return token.includes("donate") || token.includes("checkout") || token.includes("payment") || token.includes("subscribe") || token.includes("billing");
  });
}

function pathParts(url: URL): string[] {
  let path = url.pathname;
  try {
    path = decodeURIComponent(url.pathname);
  } catch {
    path = url.pathname;
  }
  return path.split("/").filter(Boolean);
}

/** eTLD+1, so blog.archive.org and archive.org count as one site. */
export function registrableDomain(host: string): string {
  const labels = host.toLowerCase().replace(/^www\./, "").replace(/\.+$/, "").split(".").filter(Boolean);
  if (labels.length <= 2) return labels.join(".");
  const last2 = labels.slice(-2).join(".");
  if (MULTI_SUFFIX.has(last2)) return labels.slice(-3).join(".");
  return last2;
}

/**
 * 2 = another registrable domain, 1 = same site but a different path,
 * 0 = same path (query only). Same-document hashes never reach here.
 */
function rankLink(url: URL, current: URL): number {
  const path = (value: string) => value.replace(/\/+$/, "") || "/";
  if (registrableDomain(url.hostname) !== registrableDomain(current.hostname)) return 2;
  if (path(url.pathname) !== path(current.pathname)) return 1;
  return 0;
}

interface Candidate {
  href: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rank: number;
  midY: number;
  index: number;
}

export interface PortalFrame {
  viewW: number;
  viewH: number;
  /** Touch controls sit in the lower corners, so a made-up spot stays clear of them. */
  touch: boolean;
}

function candidates(links: PageLink[], pageUrl: string, blocked: ReadonlySet<string>): Candidate[] {
  let current: URL;
  try {
    current = new URL(pageUrl);
  } catch {
    current = new URL("https://archive.org/");
  }
  const here = canonUrl(current.href);
  const out: Candidate[] = [];
  for (let index = 0; index < links.length; index++) {
    const link = links[index];
    const url = proxyLoadable(link.href);
    if (!url) continue;
    if (isDownload(url) || isAuth(url) || isCommerce(url)) continue;
    const key = canonUrl(url.href);
    if (!key || key === here || blocked.has(key)) continue;
    out.push({
      href: url.href,
      x: link.x,
      y: link.y,
      w: link.w,
      h: link.h,
      rank: rankLink(url, current),
      midY: link.y + link.h / 2,
      index,
    });
  }
  return out;
}

/**
 * Top of the exit band, in page pixels.
 * The band is the overlap of the bottom 20% and the last viewport: it starts
 * at whichever of those two lines sits lower on the page. A page at least two
 * viewports tall also stays out of the first two screens. A shorter page skips
 * that floor and keeps the bottom of the level.
 */
export function portalBandTop(pageH: number, viewH: number): number {
  const h = Math.max(1, pageH);
  const view = Math.max(1, Math.min(viewH > 0 ? viewH : h, h));
  const fromBottom = h * (1 - PORTAL_END_BAND);
  const lastScreen = Math.max(0, h - view);
  let top = Math.max(fromBottom, lastScreen);
  if (h >= view * 2 - 0.5) top = Math.max(top, Math.min(view * 2, Math.max(0, h - 48)));
  const latest = Math.max(0, h - 48);
  if (top > latest) top = latest;
  return top;
}

function inBand(item: Candidate, pageH: number, viewH: number): boolean {
  return item.midY >= portalBandTop(pageH, viewH) && item.midY <= pageH + 8;
}

/** Lower on the page wins. A tie prefers another site, then document order. */
function lower(a: Candidate, b: Candidate): boolean {
  if (a.midY > b.midY + 0.5) return true;
  if (b.midY > a.midY + 0.5) return false;
  if (a.rank !== b.rank) return a.rank > b.rank;
  return a.index < b.index;
}

function bottomMost(pool: Candidate[]): Candidate | null {
  let best: Candidate | null = null;
  for (const item of pool) if (!best || lower(item, best)) best = item;
  return best;
}

function fallbackHref(pageUrl: string, blocked: ReadonlySet<string>, rng: () => number): string {
  const here = canonUrl(pageUrl);
  let pool = PORTAL_FALLBACKS.filter((href) => canonUrl(href) !== here && !blocked.has(canonUrl(href)));
  if (!pool.length) pool = PORTAL_FALLBACKS.filter((href) => canonUrl(href) !== here);
  if (!pool.length) pool = PORTAL_FALLBACKS;
  return pool[Math.floor(rng() * pool.length) % pool.length];
}

/**
 * A made-up exit in the last screen, clear of the stick, hotbar, and grenade.
 * Used when no real link sits in the exit band, and when a reserved link is gone.
 */
export function endSpot(
  pageUrl: string,
  pageW: number,
  pageH: number,
  blocked: ReadonlySet<string>,
  rng: () => number,
  frame?: PortalFrame,
): PortalSpot {
  const href = fallbackHref(pageUrl, blocked, rng);
  const w = 96;
  const h = 80;
  const viewH = Math.max(1, Math.min(frame?.viewH && frame.viewH > 0 ? frame.viewH : pageH, pageH));
  const viewW = Math.max(w + 16, Math.min(pageW, frame?.viewW && frame.viewW > 0 ? frame.viewW : pageW));
  const touch = !!frame?.touch;
  const bottomInset = touch ? 148 : 96;
  const leftInset = touch ? 168 : 36;
  const rightInset = touch ? 112 : 36;
  const camY = Math.max(0, pageH - viewH);
  const safeLeft = Math.min(leftInset, Math.max(8, pageW - w - 8));
  const safeRight = Math.max(safeLeft + w, Math.min(pageW, viewW) - rightInset);
  let cx = (safeLeft + safeRight) / 2;
  cx = Math.max(w / 2 + 8, Math.min(pageW - w / 2 - 8, cx));
  const band = portalBandTop(pageH, viewH);
  const hudBottom = camY + viewH - bottomInset;
  let y = Math.min(pageH - h - 8, hudBottom - h);
  const lastTop = camY + (pageH > viewH + 24 ? 8 : 0);
  if (y < lastTop) y = lastTop;
  // Stay in the band when that still clears the HUD. The HUD wins if the two disagree.
  if (y + h * 0.5 < band && hudBottom - h >= band) y = Math.min(pageH - h - 8, Math.max(y, band));
  y = Math.max(8, Math.min(pageH - h - 8, y));
  return { href, x: cx - w / 2, y, w, h, fallback: true };
}

/** Box kept solid until the portal opens. Large footers are clamped to the exit itself. */
export function portalShield(spot: PortalSpot): { x: number; y: number; w: number; h: number } {
  const cx = spot.x + spot.w / 2;
  const cy = spot.y + spot.h / 2;
  const w = Math.max(28, Math.min(spot.w, 180));
  const h = Math.max(20, Math.min(spot.h, 120));
  const pad = 2;
  return { x: cx - w / 2 - pad, y: cy - h / 2 - pad, w: w + pad * 2, h: h + pad * 2 };
}

function linkSpot(hit: Candidate): PortalSpot {
  return { href: hit.href, x: hit.x, y: hit.y, w: hit.w, h: hit.h, fallback: false };
}

/**
 * The bottom-most loadable link in the exit band. A page shorter than two
 * viewports may use its bottom-most link even above that band. Anything else
 * is a clean spot in the last screen.
 * `blocked` holds failed hops and pages already visited this run.
 */
export function choosePortal(
  links: PageLink[],
  pageUrl: string,
  pageW: number,
  pageH: number,
  blocked: ReadonlySet<string>,
  rng: () => number = Math.random,
  frame?: PortalFrame,
): PortalSpot {
  const viewH = frame?.viewH && frame.viewH > 0 ? frame.viewH : Math.min(pageH, 800);
  const all = candidates(links, pageUrl, blocked);
  const zone = all.filter((item) => inBand(item, pageH, viewH));
  const hit = bottomMost(zone);
  if (hit) return linkSpot(hit);
  const short = pageH < Math.max(1, viewH) * 2 - 0.5;
  const low = bottomMost(all);
  if (short && low) return linkSpot(low);
  const spot = endSpot(pageUrl, pageW, pageH, blocked, rng, frame ?? { viewW: pageW, viewH, touch: false });
  // No link in the exit band: the exit still sits in the last screen, but it
  // leads where this page's lowest real link goes, so the run stays on the web
  // the player is wrecking instead of jumping to a stock page.
  if (low) spot.href = low.href;
  return spot;
}
